import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../utils/api";
import FileSlot from "../components/FileSlot";
import { ArrowLeft, Play, X, Plus, AlertCircle, Settings, Check, Wand2 } from "lucide-react";

/**
 * Multi-file Hub-and-Spoke Reconciliation Wizard.
 * 1 anchor file + N target files, each with its own rule set.
 */

function FileSlotAdapter({ file, onChange, onClear, accent, label, testIdPrefix }) {
  return (
    <FileSlot
      file={file}
      onChange={onChange}
      onClear={onClear}
      accent={accent}
      label={label}
      compact
      testIdPrefix={testIdPrefix}
    />
  );
}

export default function MultiReconciliationWizard() {
  const [name, setName] = useState("");
  const [anchor, setAnchor] = useState(null);
  const [anchorTsId, setAnchorTsId] = useState("");
  const [legs, setLegs] = useState([{ file: null, rule_set_id: "", transform_set_id: "", anchor_source: "original", cascade_include_partials: false }]);
  const [ruleSets, setRuleSets] = useState([]);
  const [transformSets, setTransformSets] = useState([]);
  const [cap, setCap] = useState(10);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [capEditOpen, setCapEditOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/rule-sets/").then(r => setRuleSets(r.data || []));
    api.get("/transform-sets/").then(r => setTransformSets(r.data || [])).catch(() => {});
    api.get("/multi-runs/settings").then(r => setCap(r.data.multi_file_cap || 10)).catch(() => {});
    api.get("/auth/me").then(r => setUser(r.data)).catch(() => {});
  }, []);

  const addLeg = () => {
    if (legs.length >= cap) { setError(`File cap reached (${cap}). Admin can raise under Settings.`); return; }
    setLegs([...legs, { file: null, rule_set_id: "", transform_set_id: "", anchor_source: "original", cascade_include_partials: false }]);
  };
  const removeLeg = (i) => setLegs(legs.filter((_, idx) => idx !== i));
  const updateLeg = (i, patch) => setLegs(legs.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const canSubmit = !!name.trim() && !!anchor && legs.length > 0 && legs.every(l => l.file && l.rule_set_id);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setError("");
    try {
      const payload = {
        name: name.trim(),
        anchor_files: [
          { file_id: anchor.file_id, sheet_name: anchor.sheet || null, skip_rows: Number(anchor.skip_rows || 0) },
          ...(anchor.extra_files || []).map(ef => ({ file_id: ef.file_id, sheet_name: ef.sheet || null, skip_rows: Number(anchor.skip_rows || 0), col_mapping: ef.col_mapping || {} })),
        ],
        anchor_merge_mode: anchor.merge_mode || "superset",
        anchor_transform_set_id: anchorTsId || null,
        legs: legs.map(l => ({
          target_files: [
            { file_id: l.file.file_id, sheet_name: l.file.sheet || null, skip_rows: Number(l.file.skip_rows || 0) },
            ...(l.file.extra_files || []).map(ef => ({ file_id: ef.file_id, sheet_name: ef.sheet || null, skip_rows: Number(l.file.skip_rows || 0), col_mapping: ef.col_mapping || {} })),
          ],
          target_merge_mode: l.file.merge_mode || "superset",
          rule_set_id: l.rule_set_id,
          target_transform_set_id: l.transform_set_id || null,
          anchor_source: l.anchor_source || "original",
          cascade_include_partials: !!l.cascade_include_partials,
        })),
      };
      const res = await api.post("/multi-runs/run", payload);
      navigate(`/multi-results/${res.data.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  const saveCap = async (newCap) => {
    try {
      const res = await api.put("/multi-runs/settings", { multi_file_cap: Number(newCap) });
      setCap(res.data.multi_file_cap);
      setCapEditOpen(false);
    } catch (e) {
      alert(`Failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const isAdmin = user && (user.role === "superadmin" || user.role === "dept_admin");

  return (
    <Layout>
      <div className="animate-in" style={{ maxWidth: "900px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><ArrowLeft size={18} /></button>
            <div>
              <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.5rem", margin: 0 }}>Multi-file Reconciliation</h1>
              <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", margin: 0 }}>
                Reconcile 1 anchor file against up to {cap} target files.
              </p>
            </div>
          </div>
          {isAdmin && (
            <button data-testid="multi-settings-btn" onClick={() => setCapEditOpen(true)}
              className="btn-secondary"
              style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.78rem", padding: "0.3rem 0.7rem" }}>
              <Settings size={12} /> File cap: {cap}
            </button>
          )}
        </div>

        <div className="recon-card" style={{ padding: "1.25rem", marginBottom: "1rem" }}>
          <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: "0.35rem" }}>Run name</label>
          <input data-testid="multi-name" className="recon-input" value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. Broker reconciliation 2026-02"
            style={{ width: "100%", fontSize: "0.9rem" }} />
        </div>

        <div className="recon-card" style={{ padding: "1.25rem", marginBottom: "1rem", borderLeft: "3px solid var(--sage)" }}>
          <div style={{ marginBottom: "0.5rem", fontSize: "0.72rem", fontWeight: 700, color: "var(--sage)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Anchor file (e.g. bank statement)
          </div>
          <FileSlot file={anchor} onChange={f => setAnchor(f)}
            onClear={() => setAnchor(null)}
            label="Drop your anchor file" accent="var(--sage)" />
          {transformSets.length > 0 && (
            <div style={{ marginTop: "0.625rem" }}>
              <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: "0.35rem", marginBottom: "0.3rem" }}>
                <Wand2 size={11} /> Transform Set (optional)
              </label>
              <select data-testid="anchor-ts-select" className="recon-input" style={{ width: "100%" }}
                value={anchorTsId} onChange={e => setAnchorTsId(e.target.value)}>
                <option value="">— none —</option>
                {transformSets.map(ts => <option key={ts.id} value={ts.id}>{ts.name}</option>)}
              </select>
            </div>
          )}
        </div>

        <div className="recon-card" style={{ padding: "1.25rem", marginBottom: "1rem", borderLeft: "3px solid #1E40AF" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#1E40AF", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Target files ({legs.length}/{cap}) — each can have its own format + rule set
            </div>
            <button data-testid="add-leg-btn" onClick={addLeg} disabled={legs.length >= cap}
              className="btn-secondary"
              style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.78rem", padding: "0.3rem 0.65rem", opacity: legs.length >= cap ? 0.5 : 1 }}>
              <Plus size={12} /> Add target file
            </button>
          </div>
          {legs.map((l, i) => (
            <div key={i} data-testid={`leg-${i}`} style={{ marginBottom: "0.75rem", padding: i > 0 ? "0.5rem 0.75rem" : 0, background: i > 0 && l.anchor_source === "previous_unmatched" ? "rgba(30,64,175,0.04)" : "transparent", borderRadius: "0.5rem", border: i > 0 && l.anchor_source === "previous_unmatched" ? "1px dashed rgba(30,64,175,0.3)" : "none" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "0.5rem", alignItems: "flex-start" }}>
              <FileSlotAdapter
                testIdPrefix={`leg-${i}`}
                label={`Target #${i + 1}`}
                accent="#1E40AF"
                file={l.file}
                onChange={f => updateLeg(i, { file: f })}
                onClear={() => updateLeg(i, { file: null })}
              />
              <div>
                <label style={{ fontSize: "0.72rem", fontWeight: 600, display: "block", marginBottom: "0.25rem" }}>Rule set</label>
                <select data-testid={`leg-ruleset-${i}`}
                  value={l.rule_set_id} onChange={e => updateLeg(i, { rule_set_id: e.target.value })}
                  className="recon-input" style={{ width: "100%", fontSize: "0.85rem" }}>
                  <option value="">— select —</option>
                  {ruleSets.map(rs => <option key={rs.id} value={rs.id}>{rs.name}</option>)}
                </select>
                {transformSets.length > 0 && (
                  <div style={{ marginTop: "0.4rem" }}>
                    <label style={{ fontSize: "0.68rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: "0.3rem", marginBottom: "0.2rem" }}>
                      <Wand2 size={10} /> Transform Set
                    </label>
                    <select data-testid={`leg-ts-${i}`} className="recon-input"
                      style={{ width: "100%", fontSize: "0.82rem" }}
                      value={l.transform_set_id || ""} onChange={e => updateLeg(i, { transform_set_id: e.target.value })}>
                      <option value="">— none —</option>
                      {transformSets.map(ts => <option key={ts.id} value={ts.id}>{ts.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
              {legs.length > 1 && (
                <button data-testid={`remove-leg-${i}`} onClick={() => removeLeg(i)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", alignSelf: "center", padding: "0.25rem" }}>
                  <X size={16} />
                </button>
              )}
              </div>

              {/* ── Cascade-mode controls (leg 2+) ────────────────────── */}
              {i > 0 && (
                <div data-testid={`leg-cascade-${i}`}
                  style={{ marginTop: "0.5rem", paddingTop: "0.5rem", borderTop: "1px solid rgba(0,0,0,0.06)",
                           display: "flex", gap: "1.25rem", alignItems: "center", flexWrap: "wrap",
                           fontSize: "0.78rem", color: "var(--text-secondary)" }}>
                  <span style={{ fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontSize: "0.68rem" }}>
                    Anchor rows for this leg:
                  </span>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", cursor: "pointer" }}>
                    <input type="radio"
                      data-testid={`leg-${i}-anchor-original`}
                      checked={l.anchor_source === "original"}
                      onChange={() => updateLeg(i, { anchor_source: "original", cascade_include_partials: false })} />
                    Use full anchor file
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", cursor: "pointer" }}
                    title="Feed only the rows the previous leg didn't resolve — classic 'first try bank, then credit card' ledger flow.">
                    <input type="radio"
                      data-testid={`leg-${i}-anchor-previous`}
                      checked={l.anchor_source === "previous_unmatched"}
                      onChange={() => updateLeg(i, { anchor_source: "previous_unmatched" })} />
                    Only the unmatched rows from leg {i}
                  </label>
                  {l.anchor_source === "previous_unmatched" && (
                    <label data-testid={`leg-${i}-include-partials-label`}
                      style={{ display: "flex", alignItems: "center", gap: "0.35rem", cursor: "pointer",
                               marginLeft: "0.5rem", paddingLeft: "0.75rem", borderLeft: "1px solid rgba(0,0,0,0.08)" }}
                      title="Also carry partial matches forward. Off by default — partials are usually worth reviewing manually.">
                      <input type="checkbox"
                        data-testid={`leg-${i}-include-partials`}
                        checked={!!l.cascade_include_partials}
                        onChange={e => updateLeg(i, { cascade_include_partials: e.target.checked })} />
                      + carry partials
                    </label>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {error && (
          <div style={{ padding: "0.6rem 0.9rem", background: "#FEE2E2", border: "1px solid #FCA5A5", borderRadius: "0.5rem", color: "#991B1B", fontSize: "0.82rem", marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
          <button onClick={() => navigate(-1)} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
          <button data-testid="multi-submit" onClick={submit} disabled={!canSubmit || submitting}
            className="btn-primary"
            style={{ fontSize: "0.9rem", padding: "0.55rem 1.1rem", display: "flex", alignItems: "center", gap: "0.4rem", opacity: (!canSubmit || submitting) ? 0.5 : 1 }}>
            {submitting ? "Running…" : <><Play size={14} /> Run reconciliation</>}
          </button>
        </div>
      </div>

      {capEditOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => setCapEditOpen(false)} />
          <div data-testid="cap-edit-modal" style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "400px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: "0 0 0.5rem" }}>Multi-file cap</h3>
            <p style={{ fontSize: "0.8rem", color: "var(--text-secondary)", marginBottom: "0.75rem" }}>
              Maximum number of target files per multi-run. Higher values may affect run time.
            </p>
            <CapEditor initial={cap} onSave={saveCap} onCancel={() => setCapEditOpen(false)} />
          </div>
        </div>
      )}
    </Layout>
  );
}

function CapEditor({ initial, onSave, onCancel }) {
  const [v, setV] = useState(initial);
  return (
    <div>
      <input type="number" min={2} max={50} value={v} onChange={e => setV(e.target.value)}
        data-testid="cap-input"
        className="recon-input" style={{ width: "100%", fontSize: "0.95rem", marginBottom: "1rem" }} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
        <button onClick={onCancel} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
        <button data-testid="cap-save" onClick={() => onSave(v)} className="btn-primary" style={{ fontSize: "0.85rem" }}>
          <Check size={12} style={{ marginRight: "0.25rem", display: "inline" }} /> Save
        </button>
      </div>
    </div>
  );
}
